import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, Lock } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useGooglePrimaryAuth } from '@/lib/googleAuth';
import GoogleAuthSection from '@/components/auth/GoogleAuthSection';
import RangerAuthLayout from '@/components/resqly/RangerAuthLayout';
import AuthTutorialLink from '@/components/resqly/AuthTutorialLink';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export default function ProviderLogin() {
  const googlePrimary = useGooglePrimaryAuth();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [emailMode, setEmailMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [loading, setLoading] = useState(false);

  const afterAuth = (data) => {
    login(data.token, 'provider');
    const p = data.provider;
    if (!p?.category) navigate('/provider/category', { replace: true });
    else if (['incomplete', 'rejected', 'resubmit'].includes(p.approval_status)) navigate('/provider/kyc', { replace: true });
    else if (p.approval_status === 'pending') navigate('/provider/status', { replace: true });
    else navigate('/provider/dashboard', { replace: true });
  };

  const doLogin = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/auth/provider/login', { email, password });
      toast.success('Welcome back'); afterAuth(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Login failed'); } finally { setLoading(false); }
  };
  const doRegister = async () => {
    if (!email || !password) { toast.error('Email & password required'); return; }
    setLoading(true);
    try {
      const { data } = await api.post('/auth/provider/register', { email, password, name });
      toast.success('Account created'); afterAuth(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not register'); } finally { setLoading(false); }
  };
  const doForgot = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/auth/provider/forgot-password', { email });
      if (data.reset_token) {
        setResetToken(data.reset_token);
        toast.success('Use the token below to reset');
        setEmailMode('reset');
      } else {
        toast.success('If email exists, token sent');
      }
    } catch (e) { toast.error('Could not generate'); } finally { setLoading(false); }
  };
  const doReset = async () => {
    setLoading(true);
    try {
      await api.post('/auth/provider/reset-password', { email, reset_token: resetToken, new_password: newPwd });
      toast.success('Password updated. Please login.');
      setEmailMode('login'); setPassword(''); setNewPwd('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Reset failed'); } finally { setLoading(false); }
  };

  const emailProps = {
    mode: emailMode,
    onModeChange: setEmailMode,
    loading,
    email,
    setEmail,
    password,
    setPassword,
    name,
    setName,
    resetToken,
    setResetToken,
    newPwd,
    setNewPwd,
    onLogin: doLogin,
    onRegister: doRegister,
    onForgot: doForgot,
    onReset: doReset,
  };

  return (
    <RangerAuthLayout>
      {googlePrimary ? (
        <div className="space-y-4">
          <GoogleAuthSection
            role="provider"
            onAuthenticated={(data) => {
              toast.success('Welcome');
              afterAuth(data);
            }}
            googleButtonTestId="provider-google-signin"
            registerNameTestId="provider-google-name"
            registerSubmitTestId="provider-google-register"
          />
          <div className="flex items-center gap-3 my-2">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-400">or continue with email</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>
          <EmailAuth {...emailProps} />
          <AuthTutorialLink />
        </div>
      ) : (
        <div className="space-y-4">
          <EmailAuth {...emailProps} />
          <AuthTutorialLink />
        </div>
      )}
    </RangerAuthLayout>
  );
}

function EmailAuth({
  mode,
  onModeChange,
  loading,
  email,
  setEmail,
  password,
  setPassword,
  name,
  setName,
  resetToken,
  setResetToken,
  newPwd,
  setNewPwd,
  onLogin,
  onRegister,
  onForgot,
  onReset,
}) {
  return (
    <div className="space-y-3">
      {mode === 'login' && (
        <>
          <Label>Email</Label>
          <InputBox icon={<Mail className="w-4 h-4 text-slate-400" />}>
            <input
              data-testid="provider-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 outline-none"
              placeholder="you@clinic.com"
            />
          </InputBox>
          <Label>Password</Label>
          <InputBox icon={<Lock className="w-4 h-4 text-slate-400" />}>
            <input
              data-testid="provider-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="flex-1 outline-none"
              placeholder="••••••••"
            />
          </InputBox>
          <Button
            data-testid="provider-login-submit"
            disabled={loading}
            onClick={onLogin}
            className="w-full bg-blue-700 hover:bg-blue-800"
          >
            {loading ? 'Signing in...' : 'Login'}
          </Button>
          <div className="flex justify-between text-xs mt-1">
            <button type="button" onClick={() => onModeChange('forgot')} className="text-blue-700">Forgot password?</button>
            <button
              type="button"
              data-testid="provider-switch-register"
              onClick={() => onModeChange('register')}
              className="text-blue-700"
            >
              Create account
            </button>
          </div>
        </>
      )}
      {mode === 'register' && (
        <>
          <Label>Name</Label>
          <Input data-testid="provider-reg-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" />
          <Label>Email</Label>
          <Input data-testid="provider-reg-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@clinic.com" />
          <Label>Password</Label>
          <Input data-testid="provider-reg-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 characters" />
          <Button
            data-testid="provider-register-submit"
            disabled={loading}
            onClick={onRegister}
            className="w-full bg-blue-700 hover:bg-blue-800"
          >
            {loading ? 'Creating...' : 'Create Account'}
          </Button>
          <button type="button" onClick={() => onModeChange('login')} className="text-xs text-slate-500 w-full text-center">
            Already have an account? Login
          </button>
        </>
      )}
      {mode === 'forgot' && (
        <>
          <Label>Email</Label>
          <Input data-testid="provider-forgot-email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" />
          <Button data-testid="provider-forgot-submit" disabled={loading} onClick={onForgot} className="w-full bg-blue-700 hover:bg-blue-800">
            Get Reset Token
          </Button>
          <button type="button" onClick={() => onModeChange('login')} className="text-xs text-slate-500 w-full text-center">
            Back to login
          </button>
        </>
      )}
      {mode === 'reset' && (
        <>
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded p-2">Demo token: <b>{resetToken}</b></p>
          <Label>Email</Label>
          <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" />
          <Label>Reset Token</Label>
          <Input value={resetToken} onChange={(e) => setResetToken(e.target.value)} />
          <Label>New Password</Label>
          <Input value={newPwd} onChange={(e) => setNewPwd(e.target.value)} type="password" />
          <Button disabled={loading} onClick={onReset} className="w-full bg-blue-700 hover:bg-blue-800">
            Reset Password
          </Button>
        </>
      )}
    </div>
  );
}

function Label({ children }) {
  return <label className="text-xs font-medium text-slate-500">{children}</label>;
}

function InputBox({ icon, children }) {
  return <div className="flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-2.5">{icon}{children}</div>;
}